import { Banner, Text } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { useCallback, useEffect, useRef, useState } from 'react';

import { axiosInstance } from 'app/app.api';
import styles from './clientRefreshBanner.module.scss';

/**
 * Renders a banner prompting the user to reload when a response interceptor flags the client as outdated.
 * Based on a backend controlled min agent-interface version header.
 */
export const ClientRefreshBanner = ({
  className,
}: PropsWithClassName): JSX.Element | null => {
  const isClientOutdatedRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const interceptorId = axiosInstance.interceptors.response.use(
      (response) => {
        if (isClientOutdatedRef.current) {
          return response;
        }

        const version = response.headers['x-dixa-min-agent-interface-version'];
        if (
          typeof version === 'string' &&
          // TIMESTAMP is a global variable set at build time in ISO format
          Date.parse(TIMESTAMP) < Date.parse(version)
        ) {
          isClientOutdatedRef.current = true;
          setIsVisible(true);
        }
        return response;
      },
    );

    return () => axiosInstance.interceptors.response.eject(interceptorId);
  }, []);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <Banner
      className={classNames(styles.root, className)}
      variant="warning"
      description={
        <Text.Paragraph variant="4">
          An important update is available. Please reload to ensure Dixa
          continues working as expected.
        </Text.Paragraph>
      }
      actionSlot={
        <>
          <Text.ExternalLink
            className={classNames(styles.link, styles.linkHighlighted)}
            onClick={handleReload}
          >
            Reload now
          </Text.ExternalLink>

          <Text.ExternalLink className={styles.link} onClick={handleDismiss}>
            Later
          </Text.ExternalLink>
        </>
      }
    />
  );
};
